import { useEffect, useState } from 'react'
import type {
  HuntType,
  TimeBlock,
  TimeBlockFormValues,
  TimeBlockPeriod,
  TimeBlockPeriodOption,
  TimeBlockSheetMode,
} from '@/shared/domain'
import { HUNT_TYPES, huntTypeLabel } from '@/shared/domain'
import { NumberInput } from '../number-input'
import { TimePicker } from '../time-picker'
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Switch,
} from '@/shared/ui/primitives'

export type TimeBlockFormSheetProps = {
  isOpen: boolean
  mode: TimeBlockSheetMode
  editingBlock: TimeBlock | null
  periodOptions: TimeBlockPeriodOption[]
  isSaving: boolean
  formError: string | null
  onClose: () => void
  onSubmit: (values: TimeBlockFormValues) => void
}

type FieldErrors = {
  name?: string
  time?: string
}

export function TimeBlockFormSheet({
  isOpen,
  mode,
  editingBlock,
  periodOptions,
  isSaving,
  formError,
  onClose,
  onSubmit,
}: TimeBlockFormSheetProps) {
  const [huntType, setHuntType] = useState<HuntType>(HUNT_TYPES[0])
  const [name, setName] = useState<string>('')
  const [startTime, setStartTime] = useState<string>('')
  const [endTime, setEndTime] = useState<string>('')
  const [timePeriod, setTimePeriod] = useState<TimeBlockPeriod>('am')
  const [enabled, setEnabled] = useState<boolean>(true)
  const [displayOrder, setDisplayOrder] = useState<number | undefined>(undefined)
  const [errors, setErrors] = useState<FieldErrors>({})

  useEffect(() => {
    if (!isOpen) return
    if (mode === 'edit' && editingBlock) {
      setHuntType(editingBlock.huntType)
      setName(editingBlock.name)
      setStartTime(editingBlock.startTime)
      setEndTime(editingBlock.endTime)
      setTimePeriod(editingBlock.timePeriod)
      setEnabled(editingBlock.enabled)
      setDisplayOrder(editingBlock.displayOrder)
    } else {
      setHuntType(HUNT_TYPES[0])
      setName('')
      setStartTime('')
      setEndTime('')
      setTimePeriod('am')
      setEnabled(true)
      setDisplayOrder(undefined)
    }
    setErrors({})
  }, [isOpen, mode, editingBlock])

  const handleSubmit = () => {
    const nextErrors: FieldErrors = {}
    if (!name.trim()) nextErrors.name = 'Name is required'
    if (!startTime || !endTime) {
      nextErrors.time = 'A start time and an end time are required'
    } else if (endTime <= startTime) {
      nextErrors.time = 'The end time must be after the start time'
    }
    setErrors(nextErrors)
    if (nextErrors.name || nextErrors.time) return

    onSubmit({
      huntType,
      name: name.trim(),
      startTime,
      endTime,
      timePeriod,
      enabled,
      displayOrder,
    })
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose()
  }

  const isEdit = mode === 'edit'

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{isEdit ? 'Edit time block' : 'Add time block'}</SheetTitle>
          <SheetDescription>
            Name a window of the day for one hunt type. Members pick it instead of the
            platform time period when they log or book that hunt type.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-4 px-4 pb-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="time-block-hunt-type">Hunt type</Label>
            <Select
              value={huntType}
              disabled={isEdit}
              onValueChange={(value) => setHuntType(value as HuntType)}
            >
              <SelectTrigger id="time-block-hunt-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HUNT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {huntTypeLabel(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {isEdit
                ? 'The hunt type is fixed once a time block is created.'
                : 'Time blocks apply to one hunt type. Hunts logged without a hunt type are never affected.'}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="time-block-name">Name</Label>
            <Input
              id="time-block-name"
              value={name}
              maxLength={80}
              placeholder="Morning Flight"
              aria-invalid={errors.name ? true : undefined}
              onChange={(event) => setName(event.target.value)}
            />
            {errors.name ? (
              <p role="alert" className="text-sm text-destructive">
                {errors.name}
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TimePicker
              id="time-block-start-time"
              label="Start time"
              required
              value={startTime}
              onChange={setStartTime}
            />
            <TimePicker
              id="time-block-end-time"
              label="End time"
              required
              value={endTime}
              onChange={setEndTime}
            />
          </div>
          {errors.time ? (
            <p role="alert" className="text-sm text-destructive">
              {errors.time}
            </p>
          ) : null}

          <div className="flex flex-col gap-2">
            <Label htmlFor="time-block-time-period">Platform time period</Label>
            <Select
              value={timePeriod}
              onValueChange={(value) => setTimePeriod(value as TimeBlockPeriod)}
            >
              <SelectTrigger id="time-block-time-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {periodOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Stats and reports keep using this period. One hunt type can map each period
              once.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="time-block-display-order">Display order</Label>
            <NumberInput
              id="time-block-display-order"
              mode="integer"
              min={0}
              value={displayOrder?.toString() ?? ''}
              placeholder="Leave blank for the default order"
              onChange={(next) => {
                if (next === '') {
                  setDisplayOrder(undefined)
                  return
                }
                const parsed = Number.parseInt(next, 10)
                setDisplayOrder(Number.isNaN(parsed) ? undefined : parsed)
              }}
            />
          </div>

          <div className="flex items-center gap-3 rounded-md border px-4 py-3">
            <Switch
              id="time-block-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
              aria-label="Enabled"
            />
            <div className="min-w-0 flex-1">
              <Label htmlFor="time-block-enabled">Enabled</Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Disabled time blocks stay configured but members can&apos;t pick them.
              </p>
            </div>
          </div>

          {formError ? (
            <p role="alert" className="text-sm text-destructive">
              {formError}
            </p>
          ) : null}
        </div>

        <SheetFooter className="flex-row items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
